import { useRef, useState } from "react";
import { trackAtlasEvent, trackAtlasEventOnce } from "../analytics";
import { ArrowIcon, HeadphonesIcon } from "../icons";
import { Figure, Lightbox, PageBeacon, PageFooter } from "../components";
import { getEssayAudio } from "../essayAudio";
import { getHomeIllustration } from "../illustrations";
import { useLocale } from "../locale";
import { Link } from "../navigation";
import { ReaderContinuityPanel } from "../ReaderContinuity";
import {
  readerRouteSpecs,
  resolveReaderCrystal,
  resolveReaderMaterial,
} from "../readerRoutes";
import { recordReaderStep } from "../readerState";

const homeCopy = {
  ru: {
    hero: {
      eyebrow: "18 глав · 54 идеи с явным статусом и границей · 6 больших эссе",
      beaconLabel: "Введение",
      title: "Понимание не делает мир проще",
      lead: "Оно проясняет, какие силы в нём действуют, как они связаны, что остаётся неизвестным и где проходят границы нашего знания и влияния.",
      start: "Выбрать точку входа",
      method: "Как устроен Атлас",
      promiseLabel: "Главное обещание",
      promise: "Прежде чем предлагать ответы, Атлас помогает различить вещи, которые выглядят похожими, но требуют разных решений: факт и толкование, влияние и контроль, риск и неопределённость, успех и достойную цель. Пока эти вещи смешаны, даже очень умный ответ может оказаться ответом не на тот вопрос.",
    },
    readerRoutes: {
      kicker: "Три точки входа",
      title: "Начните с того, что происходит с вами сейчас",
      intro: "Атлас не обязательно читать подряд. Каждый короткий маршрут соединяет четыре уже существующих материала и заканчивается одним вопросом к вашей ситуации.",
      chapter: "Глава",
      essay: "Эссе",
      step: "Шаг",
      practice: "Практика",
      context: "Открыть идею и контекст",
      choices: {
        seeing: {
          title: "Хочу лучше видеть реальность",
          lead: "Отделить наблюдаемый след от рассказа, увидеть повторяющуюся структуру и заметить, что теряется, когда реальность переводят в данные.",
          caution: "",
        },
        decision: {
          title: "Мне нужно принять трудное решение",
          lead: "Отделить качество выбора от будущего исхода, назвать разные блага, ограничить необратимый вред и сделать цену отказа видимой.",
          caution: "",
        },
        limits: {
          title: "Я столкнулся с пределом или потерей контроля",
          lead: "Увидеть, как изменились реальные возможности действия, сохранить слышимость воли и распознать ценность помощи без обещания победы.",
          caution: "Это философская карта различений, а не медицинское или кризисное руководство.",
        },
      },
    },
    orientation: {
      kicker: "Куда вы попали",
      title: "Это не теория всего. Это мастерская карт человеческой реальности",
      lead: "У событий есть причины, факты можно устанавливать, а материальный мир сопротивляется нашим желаниям. Но человек никогда не встречает этот мир без посредников: тело, память, язык, страх, надежда и привязанность влияют на то, что он заметит и какой смысл этому придаст.",
      loopAria: "Причинная петля человеческого действия",
      loop: ["Реальность", "опыт", "модель мира", "выбор", "действие", "изменившаяся реальность"],
      paragraphs: [
        "Люди действуют не из самой реальности, а из понятой ими версии реальности. Поэтому ложный слух способен вызвать настоящее банкротство, воображаемое унижение — реальный конфликт, а надежда — удержать группу от распада. Ошибочная модель не отменяет причинность: войдя в поступок, она сама становится причиной.",
        "Миллионы таких петель наблюдают друг друга, создают правила и меняются под воздействием чужих прогнозов. Поэтому причинный мир не обязательно является полностью предсказуемым, особенно когда участники способны истолковать прогноз и ответить на него.",
        "Атлас не обещает вычислить человека. Он помогает определить, что здесь наблюдалось, что было достроено рассказом, где действует личный выбор, где — устройство отношений или института, а где выбранная модель уже перестала видеть живого человека.",
        "Его задача скромнее полной теории и важнее красивой формулы: понимать достаточно, чтобы действовать разумнее, сохраняя различие между тем, что можно установить, объяснить, предположить и только спросить у того, кто проживает эту реальность изнутри.",
      ],
      axiom: "Хорошая карта показывает не только путь. Она показывает границу, за которой должна уступить место вопросу, свидетельству и живому опыту.",
    },
    manifesto: {
      kicker: "Зачем нужен ещё один Атлас",
      title: "Не коллекция умных фраз, а грамматика различений",
      large: "Большинство опасных ошибок выглядит не как глупость. Оно выглядит как разумная мысль, применённая не к тому объекту, не на том уровне или без границы.",
      paragraphs: [
        "Можно точно посчитать показатель и ошибиться в цели. Можно хорошо прочитать мотив человека и не заметить правило, которое заставляет разных людей вести себя одинаково. Можно выиграть спор и разрушить способность действовать вместе.",
        "Поэтому каждая важная идея проходит восемь проверок: какое различение вводит, какой механизм описывает, на каком уровне работает, при каких условиях верна, где ломается, какое сильное возражение её ограничивает, какой пример проясняет и какой вопрос к действию открывает.",
      ],
    },
    distinctionsHeading: {
      kicker: "Базовый инструмент",
      title: "Восемь различий, которые возвращают ясность",
      intro: "Они не решают ситуацию вместо вас. Они не дают двум разным вопросам незаметно ответить одним словом.",
    },
    distinctions: [
      ["Факт", "толкование", "Первое ограничивает рассказ; второе связывает наблюдения и может быть ошибочным."],
      ["Причинный механизм", "пусковое событие", "Механизм объясняет, почему результат становится возможным или повторяется; пусковое событие начинает конкретную цепь. Увольнение может запустить протест, но его повторяемость объясняют накопленные условия, правила и ожидания."],
      ["Влияние", "контроль", "Влияние меняет вероятность; контроль позволяет при известных условиях удерживать существенный результат в заданных пределах и требует обратной связи. В сложной человеческой системе второе встречается реже первого."],
      ["Власть", "право", "Способность добиться своего ещё не объясняет, почему это допустимо и кто мог возразить."],
      ["Результат", "качество решения", "Удачный исход не оправдывает беспечность, а плохой не всегда доказывает ошибку процесса."],
      ["Цена", "ценность", "Цена показывает отданное; ценность требует ответа, ради чего и для кого эта отдача оправдана."],
      ["Игра", "жизнь", "В игре можно различить ходы и зависимость игроков. Горю, любви, телу и смертности такой язык подходит не всегда."],
      ["Согласие", "отсутствие отказа", "Молчание под зависимостью не равно свободному согласию с возможностью безопасно сказать «нет»."],
    ],
    partsHeading: {
      kicker: "Маршрут",
      title: "Шесть движений от наблюдения к пределу",
      intro: "Порядок имеет значение: сначала увидеть, затем выбрать; сначала понять взаимодействие, затем судить о победе.",
    },
    tensionsHeading: {
      kicker: "Стоя на плечах гигантов",
      title: "Великие не дают одного ответа — они исправляют слепоту друг друга",
      intro: "Атлас не прячет противоречия под красивым «синтезом». Напряжение между оптиками часто и есть источник более зрелого суждения.",
    },
    tensions: [
      { pair: "Поппер ↔ Кун", title: "Проверка и рамка", text: "Утверждение обязано рисковать ошибкой; но сам способ увидеть ошибку уже организован понятиями и практикой сообщества." },
      { pair: "Канеман ↔ Гигеренцер", title: "Ошибка и приспособленность", text: "Эвристика может искажать суждение, а может быть точным ответом на среду. Вопрос — где и с какой обратной связью она возникла." },
      { pair: "Арендт ↔ Фуко", title: "Совместное действие и управление полем", text: "Власть рождается, когда люди действуют вместе, и работает глубже, когда формирует норму, категорию и допустимый вопрос." },
      { pair: "Макиавелли ↔ Кант", title: "Действенность и предел", text: "Намерения не спасают от последствий; последствия не дают права превратить человека только в средство." },
      { pair: "Эпиктет ↔ Сен", title: "Внутренний ответ и реальная возможность", text: "Полезно различать доступный ответ и внешний мир; опасно забывать, что ресурсы и институты распределяют саму способность выбирать." },
      { pair: "Франкл ↔ Леви", title: "Смысл и свидетельство", text: "Смысл иногда создаётся вопреки страданию; страдание не обязано иметь смысл и не даёт права требовать героизма от пострадавшего." },
    ],
    essaysHeading: {
      kicker: "Главный маршрут Атласа · 6 эссе",
      title: "Шесть эссе — один путь от знания к человеческому пределу",
      intro: "Эти эссе можно читать по отдельности, но вместе они образуют одно последовательное исследование. Сначала мы проверяем собственную карту мира, затем — власть, которая формирует общую реальность, после — границы игр, стратегии и победы. Завершается путь там, где язык контроля должен уступить место достоинству, заботе и признанию конечности человеческой жизни.",
      steps: [
        { stage: "Знание", text: "Как отличать след реальности от рассказа о нём — и строить карту, которая допускает собственную ошибку и умеет исправляться." },
        { stage: "Власть", text: "Как власть решает, что станет видимым, какие категории будут приняты и чья плохая новость сможет быть услышана." },
        { stage: "Игры", text: "Где язык игры точно показывает взаимные ходы, а где он искажает любовь, заботу, принуждение, горе и достоинство." },
        { stage: "Выбор", text: "Как выбранное направление становится стратегией через диагноз главного препятствия, согласованные действия, принятую цену и ясный предел допустимого." },
        { stage: "Победа", text: "Почему успех меняет интересы, окружение и доступ к правде — и превращает прежнее решение в новую задачу." },
        { stage: "Предел", text: "Что остаётся, когда исход уже нельзя выиграть: доступный человеку ответ, помощь, присутствие и достоинство, не зависящее от победы." },
      ],
      route: "Общий путь: увидеть границы своей карты → заметить власть над видимым → распознать границы игры → выбрать и отказаться → выдержать последствия победы → остановить язык выигрыша перед человеческим пределом.",
      read: "Читать эссе",
      begin: "Начать с эссе 01",
    },
    start: {
      kicker: "Начало",
      title: "Первая дисциплина — не верить даже хорошей карте безусловно",
      text: "Карта полезна, потому что меньше территории. Она становится опасной, когда скрывает собственный отбор. Поэтому начнём не с власти, стратегии или успеха, а с простого вопроса: как реальность оставляет след — и как ум превращает следы в рассказ.",
    },
  },
  uk: {
    hero: {
      eyebrow: "18 розділів · 54 ідеї з явним статусом і межею · 6 великих есе",
      beaconLabel: "Вступ",
      title: "Розуміння не робить світ простішим",
      lead: "Воно прояснює, які сили в ньому діють, як вони пов’язані, що залишається невідомим і де проходять межі нашого знання та впливу.",
      start: "Обрати точку входу",
      method: "Як побудований Атлас",
      promiseLabel: "Головна обіцянка",
      promise: "Перш ніж пропонувати відповіді, Атлас допомагає розрізнити речі, які здаються схожими, але потребують різних рішень: факт і тлумачення, вплив і контроль, ризик і невизначеність, успіх і гідну мету. Поки ці речі змішані, навіть дуже розумна відповідь може виявитися відповіддю не на те запитання.",
    },
    readerRoutes: {
      kicker: "Три точки входу",
      title: "Почніть із того, що відбувається з вами зараз",
      intro: "Атлас не обов’язково читати послідовно. Кожен короткий маршрут поєднує чотири вже наявні матеріали й завершується одним запитанням до вашої ситуації.",
      chapter: "Розділ",
      essay: "Есе",
      step: "Крок",
      practice: "Практика",
      context: "Відкрити ідею та контекст",
      choices: {
        seeing: {
          title: "Хочу краще бачити реальність",
          lead: "Відокремити спостережуваний слід від оповіді, побачити повторювану структуру й помітити, що втрачається, коли реальність перекладають мовою даних.",
          caution: "",
        },
        decision: {
          title: "Мені потрібно ухвалити складне рішення",
          lead: "Відокремити якість вибору від майбутнього результату, назвати різні блага, обмежити незворотну шкоду й зробити ціну відмови видимою.",
          caution: "",
        },
        limits: {
          title: "Я зіткнувся з межею або втратою контролю",
          lead: "Побачити, як змінилися реальні можливості дії, зберегти чутність волі й розпізнати цінність допомоги без обіцянки перемоги.",
          caution: "Це філософська карта розрізнень, а не медичне чи кризове керівництво.",
        },
      },
    },
    orientation: {
      kicker: "Куди ви потрапили",
      title: "Це не теорія всього. Це майстерня карт людської реальності",
      lead: "Події мають причини, факти можна встановлювати, а матеріальний світ чинить опір нашим бажанням. Та людина ніколи не зустрічає цей світ без посередників: тіло, пам’ять, мова, страх, надія і прив’язаність впливають на те, що вона помітить і якого сенсу цьому надасть.",
      loopAria: "Цикл людської дії та її наслідків",
      loop: ["Реальність", "досвід", "модель світу", "вибір", "дія", "змінена реальність"],
      paragraphs: [
        "Люди діють не із самої реальності, а з тієї її версії, яку зрозуміли. Тому неправдивий слух здатен спричинити справжнє банкрутство, уявлене приниження — реальний конфлікт, а надія — втримати групу від розпаду. Хибна модель не скасовує причинності: перетворившись на вчинок, вона сама стає причиною.",
        "Мільйони таких петель переплітаються: люди спостерігають одне одного, створюють правила й змінюються під впливом чужих прогнозів. Тому причинний світ не обов’язково є цілком передбачуваним, особливо коли його учасники здатні витлумачити прогноз і відповісти на нього.",
        "Атлас не обіцяє обчислити людину. Він допомагає визначити, що ми тут спостерігали, а що додала оповідь; де діє особистий вибір, де — устрій стосунків або інституції, а де обрана модель уже перестала бачити живу людину.",
        "Його завдання скромніше за повну теорію і важливіше за красиву формулу: розуміти досить, щоб діяти розумніше, розрізняючи те, що можна встановити, пояснити чи припустити, і те, про що можна лише запитати людину, яка проживає цю реальність зсередини.",
      ],
      axiom: "Добра карта показує не лише шлях. Вона показує межу, за якою сама має поступитися місцем запитанню, свідченню та живому досвіду.",
    },
    manifesto: {
      kicker: "Навіщо потрібен ще один Атлас",
      title: "Не збірка розумних фраз, а граматика розрізнень",
      large: "Більшість небезпечних помилок не схожа на дурість. Вона схожа на розумну думку, застосовану не до того об’єкта, не на тому рівні або без межі.",
      paragraphs: [
        "Можна точно порахувати показник і помилитися в меті. Можна добре прочитати мотив людини й не помітити правила, яке змушує різних людей поводитися однаково. Можна виграти суперечку і зруйнувати здатність діяти разом.",
        "Тому кожна важлива ідея проходить вісім перевірок: яке розрізнення вона вводить, який механізм описує, на якому рівні працює, за яких умов слушна, де ламається, яке сильне заперечення її обмежує, який приклад прояснює і яке запитання до дії відкриває.",
      ],
    },
    distinctionsHeading: {
      kicker: "Базовий інструмент",
      title: "Вісім розрізнень, що повертають ясність",
      intro: "Вони не розв’язують ситуацію замість вас. Вони не дають двом різним запитанням непомітно відповісти одним словом.",
    },
    distinctions: [
      ["Факт", "тлумачення", "Перший обмежує оповідь; друге пов’язує спостереження і може бути хибним."],
      ["Причинний механізм", "подія-поштовх", "Механізм пояснює, чому результат стає можливим або повторюється; подія-поштовх починає конкретний ланцюг. Звільнення може запустити протест, але його повторюваність пояснюють накопичені умови, правила й очікування."],
      ["Вплив", "контроль", "Вплив змінює ймовірність; контроль за відомих умов дає змогу утримувати суттєвий результат у заданих межах і потребує зворотного зв’язку. У складній людській системі друге трапляється рідше за перше."],
      ["Влада", "право", "Здатність домогтися свого ще не пояснює, чому це припустимо і хто мав змогу заперечити."],
      ["Результат", "якість рішення", "Вдалий результат не виправдовує безтурботності, а невдалий не завжди доводить помилковість процесу."],
      ["Ціна", "цінність", "Ціна показує, що було віддано; цінність вимагає відповіді, заради чого і для кого ця віддача виправдана."],
      ["Гра", "життя", "У грі можна розрізнити ходи та взаємозалежність гравців. До горя, любові, тіла й смертності така мова пасує не завжди."],
      ["Згода", "відсутність відмови", "Мовчання в умовах залежності не дорівнює вільній згоді з безпечною можливістю сказати «ні»."],
    ],
    partsHeading: {
      kicker: "Маршрут",
      title: "Шість частин: від спостереження до межі",
      intro: "Порядок має значення: спершу побачити, потім обрати; спершу зрозуміти взаємодію, потім судити про перемогу.",
    },
    tensionsHeading: {
      kicker: "Стоячи на плечах гігантів",
      title: "Видатні мислителі не дають однієї відповіді — вони допомагають побачити сліпі зони одне одного",
      intro: "Атлас не ховає суперечностей під красивим «синтезом». Напруга між різними оптиками часто і є джерелом зрілішого судження.",
    },
    tensions: [
      { pair: "Поппер ↔ Кун", title: "Перевірка й рамка", text: "Твердження мусить ризикувати виявитися хибним; однак сам спосіб побачити помилку вже організований поняттями й практикою спільноти." },
      { pair: "Канеман ↔ Ґіґеренцер", title: "Помилка і пристосованість", text: "Евристика може спотворювати судження, а може бути точною відповіддю на середовище. Питання в тому, де вона виникла і який зворотний зв’язок її сформував." },
      { pair: "Арендт ↔ Фуко", title: "Спільна дія та формування поля", text: "Влада народжується, коли люди діють разом, і працює глибше, коли формує норму, категорію та припустиме запитання." },
      { pair: "Макіавеллі ↔ Кант", title: "Дієвість і межа", text: "Наміри не рятують від наслідків; наслідки не дають права перетворити людину лише на засіб." },
      { pair: "Епіктет ↔ Сен", title: "Внутрішня відповідь і реальна можливість", text: "Корисно розрізняти доступну нам відповідь і зовнішній світ; небезпечно забувати, що ресурси й інституції розподіляють саму здатність обирати." },
      { pair: "Франкл ↔ Леві", title: "Сенс і свідчення", text: "Сенс іноді створюють усупереч стражданню; страждання не зобов’язане мати сенс і не дає права вимагати героїзму від того, хто його зазнає." },
    ],
    essaysHeading: {
      kicker: "Головний маршрут Атласу · 6 есе",
      title: "Шість есе — один шлях від знання до людської межі",
      intro: "Ці есе можна читати окремо, але разом вони утворюють одне послідовне дослідження. Спершу ми перевіряємо власну мапу світу, далі — владу, яка формує спільну реальність, потім — межі ігор, стратегії та перемоги. Шлях завершується там, де мова контролю має поступитися гідності, турботі й визнанню скінченності людського життя.",
      steps: [
        { stage: "Знання", text: "Як відрізняти слід реальності від оповіді про нього — і будувати мапу, здатну визнавати власну помилку та виправлятися." },
        { stage: "Влада", text: "Як влада вирішує, що стане видимим, які категорії будуть прийняті та чию погану новину зможуть почути." },
        { stage: "Ігри", text: "Де мова гри точно показує взаємні ходи, а де спотворює любов, турботу, примус, горе й гідність." },
        { stage: "Вибір", text: "Як обраний напрям стає стратегією через діагноз головної перешкоди, узгоджені дії, прийняту ціну та ясну межу допустимого." },
        { stage: "Перемога", text: "Чому успіх змінює інтереси, оточення й доступ до правди — і перетворює колишнє рішення на нове завдання." },
        { stage: "Межа", text: "Що залишається, коли результат уже не можна виграти: доступна людині відповідь, допомога, присутність і гідність, що не залежить від перемоги." },
      ],
      route: "Спільний шлях: побачити межі своєї мапи → помітити владу над видимим → розпізнати межі гри → обрати й відмовитися → витримати наслідки перемоги → зупинити мову виграшу перед людською межею.",
      read: "Читати есе",
      begin: "Почати з есе 01",
    },
    start: {
      kicker: "Початок",
      title: "Перша дисципліна — не довіряти безумовно навіть добрій карті",
      text: "Карта корисна, бо вона менша за територію. Вона стає небезпечною, коли приховує власний відбір. Тому почнемо не з влади, стратегії чи успіху, а з простого запитання: як реальність залишає слід — і як розум перетворює сліди на оповідь.",
    },
  },
  en: {
    hero: {
      eyebrow: "18 chapters · 54 ideas with an explicit status and boundary · 6 major essays",
      beaconLabel: "Introduction",
      title: "Understanding does not make the world simpler",
      lead: "It clarifies which forces are at work, how they are connected, what remains unknown, and where the limits of our knowledge and influence lie.",
      start: "Choose where to begin",
      method: "How the Atlas works",
      promiseLabel: "Central promise",
      promise: "Before offering answers, the Atlas helps distinguish things that look alike but require different decisions: fact and interpretation, influence and control, risk and uncertainty, success and a worthy aim. While these remain confused, even a very intelligent answer may be answering the wrong question.",
    },
    readerRoutes: {
      kicker: "Three ways in",
      title: "Begin with what is happening now",
      intro: "The Atlas does not have to be read in order. Each short route joins four existing materials and ends with one question for your situation.",
      chapter: "Chapter",
      essay: "Essay",
      step: "Step",
      practice: "Practice",
      context: "Open the idea and its context",
      choices: {
        seeing: {
          title: "I want to see reality more clearly",
          lead: "Separate an observed trace from the story around it, notice a recurring structure, and see what is lost when reality is translated into data.",
          caution: "",
        },
        decision: {
          title: "I need to make a difficult decision",
          lead: "Separate the quality of a choice from its later outcome, name distinct goods, limit irreversible harm, and make the cost of refusal visible.",
          caution: "",
        },
        limits: {
          title: "I am facing a limit or loss of control",
          lead: "See how real possibilities for action have changed, keep a person’s will audible, and recognize the value of help without promising victory.",
          caution: "This is a philosophical map of distinctions, not medical or crisis guidance.",
        },
      },
    },
    orientation: {
      kicker: "What you have entered",
      title: "This is not a theory of everything. It is a workshop for mapping human reality",
      lead: "Events have causes, facts can be established, and the material world resists our wishes. Yet human beings never encounter that world without mediation: body, memory, language, fear, hope, and attachment influence what we notice and what meaning we give it.",
      loopAria: "The causal loop of human action",
      loop: ["Reality", "experience", "model of the world", "choice", "action", "changed reality"],
      paragraphs: [
        "People act not from reality itself, but from reality as they understand it. A false rumor can therefore cause a real bankruptcy, an imagined humiliation can provoke a real conflict, and hope can keep a group from falling apart. A mistaken model does not suspend causality: once embodied in action, it becomes a cause in its own right.",
        "Millions of such loops observe one another, create rules, and change in response to other people’s forecasts. A causal world is therefore not necessarily fully predictable, especially when its participants can interpret a forecast and respond to it.",
        "The Atlas does not promise to calculate a human being. It helps identify what was observed, what a story added, where personal choice is at work, where the structure of a relationship or institution is at work, and where the chosen model has stopped seeing the living person.",
        "Its task is more modest than a complete theory and more important than an elegant formula: to understand enough to act more wisely, while preserving the distinction among what can be established, explained, conjectured, and only asked of the person who lives this reality from within.",
      ],
      axiom: "A good map shows more than a path. It shows the boundary beyond which it must give way to a question, testimony, and lived experience.",
    },
    manifesto: {
      kicker: "Why another Atlas",
      title: "Not a collection of clever phrases, but a grammar of distinctions",
      large: "Most dangerous errors do not look like stupidity. They look like reasonable ideas applied to the wrong object, at the wrong level, or without a boundary.",
      paragraphs: [
        "We can calculate a metric precisely and be wrong about the aim. We can read a person’s motive well and miss the rule that makes different people behave alike. We can win an argument and destroy the ability to act together.",
        "Every important idea therefore passes eight tests: what distinction it introduces, what mechanism it describes, at which level it works, under what conditions it is true, where it breaks, which strong objection limits it, which example clarifies it, and which question for action it opens.",
      ],
    },
    distinctionsHeading: {
      kicker: "The basic instrument",
      title: "Eight distinctions that restore clarity",
      intro: "They do not resolve the situation for you. They prevent two different questions from being answered unnoticed with the same word.",
    },
    distinctions: [
      ["Fact", "interpretation", "A fact constrains the story; an interpretation connects observations and may be wrong."],
      ["Causal mechanism", "triggering event", "A mechanism explains why an outcome becomes possible or recurs; a triggering event starts a particular chain. A dismissal may trigger a protest, but accumulated conditions, rules, and expectations explain why protests keep recurring."],
      ["Influence", "control", "Influence changes a probability. Under known conditions, control keeps a significant result within specified bounds and requires feedback. In a complex human system, control is rarer than influence."],
      ["Power", "right", "The ability to get one’s way does not yet explain why doing so is permissible or who had a chance to object."],
      ["Outcome", "decision quality", "A fortunate outcome does not justify carelessness, and a bad outcome does not always prove that the decision process was flawed."],
      ["Cost", "value", "Cost shows what was given up; value requires an answer about what and whose good could justify that sacrifice."],
      ["Game", "life", "In a game, moves and interdependence among players can be distinguished. This language does not always fit grief, love, the body, or mortality."],
      ["Consent", "absence of refusal", "Silence under conditions of dependence is not free consent with a safe possibility of saying no."],
    ],
    partsHeading: {
      kicker: "The route",
      title: "Six movements from observation to the limit",
      intro: "The order matters: first see, then choose; first understand the interaction, then judge the victory.",
    },
    tensionsHeading: {
      kicker: "Standing on the shoulders of giants",
      title: "Great thinkers do not give one answer—they correct one another’s blindness",
      intro: "The Atlas does not hide contradictions beneath an elegant ‘synthesis.’ Tension between lenses is often the source of more mature judgment.",
    },
    tensions: [
      { pair: "Popper ↔ Kuhn", title: "Testing and framework", text: "A claim must risk being wrong; yet the very way an error becomes visible is already organized by a community’s concepts and practices." },
      { pair: "Kahneman ↔ Gigerenzer", title: "Error and adaptation", text: "A heuristic can distort judgment, or it can be an accurate response to an environment. The question is where it arose and what feedback shaped it." },
      { pair: "Arendt ↔ Foucault", title: "Acting together and shaping the field", text: "Power arises when people act together, and works more deeply when it shapes the norm, the category, and the questions that may be asked." },
      { pair: "Machiavelli ↔ Kant", title: "Effectiveness and limit", text: "Intentions do not rescue us from consequences; consequences do not entitle us to turn a person into a mere means." },
      { pair: "Epictetus ↔ Sen", title: "Inner response and real opportunity", text: "It is useful to distinguish our available response from the external world; it is dangerous to forget that resources and institutions distribute the very ability to choose." },
      { pair: "Frankl ↔ Levi", title: "Meaning and testimony", text: "Meaning can sometimes be created in defiance of suffering; suffering is not obliged to have meaning and does not entitle us to demand heroism from the person who bears it." },
    ],
    essaysHeading: {
      kicker: "The Atlas’s central route · 6 essays",
      title: "Six essays — one path from knowledge to human limits",
      intro: "These essays can stand alone, but together they form one continuous inquiry. They begin by testing our own maps of the world, then examine the power that shapes shared reality, and move through the limits of games, strategy, and victory. The path ends where the language of control must give way to dignity, care, and the limits of a mortal life.",
      steps: [
        { stage: "Knowledge", text: "How to distinguish reality’s traces from the story built around them—and make a map that can admit error and correct itself." },
        { stage: "Power", text: "How power decides what becomes visible, which categories become common, and whose bad news can be heard." },
        { stage: "Games", text: "Where the language of games reveals how people’s moves affect one another—and where it distorts love, care, coercion, grief, and dignity." },
        { stage: "Choice", text: "How a chosen direction becomes a strategy through diagnosis, coordinated action, accepted cost, and a clear moral boundary." },
        { stage: "Victory", text: "Why success changes interests, relationships, and access to truth—and turns yesterday’s solution into a new problem." },
        { stage: "Limit", text: "What remains when an outcome cannot be won: the response still available, help, presence, and dignity that does not depend on victory." },
      ],
      route: "The path: see the limits of your map → notice power over the visible → recognize the limits of games → choose and refuse → face what victory changes → stop the language of winning at the human limit.",
      read: "Read the essay",
      begin: "Begin with Essay 01",
    },
    start: {
      kicker: "Beginning",
      title: "The first discipline is never to trust even a good map unconditionally",
      text: "A map is useful because it is smaller than the territory. It becomes dangerous when it hides what it selected. We will therefore begin not with power, strategy, or success, but with a simple question: how does reality leave a trace, and how does the mind turn traces into a story?",
    },
  },
} as const;

export function HomePage() {
  const [image, setImage] = useState<{ src: string; alt: string; caption?: string } | null>(null);
  const beaconAnchorRef = useRef<HTMLSpanElement>(null);
  const { language, chapters, chapterMap, essays, essayMap, parts } = useLocale();
  const copy = homeCopy[language];
  const illustration = getHomeIllustration(language);

  return (
    <>
      <article className="home-page">
        <section className="page-opener hero" data-page-hero="home">
          <header className="page-opener-heading hero-heading">
            <span ref={beaconAnchorRef} className="page-beacon-sentinel" aria-hidden="true" />
            <p className="eyebrow">{copy.hero.eyebrow}</p>
            <h1>{copy.hero.title}</h1>
          </header>

          <Figure
            src={illustration.src}
            alt={illustration.alt}
            caption={illustration.caption}
            onOpen={(src, alt, caption) => setImage({ src, alt, caption })}
            className="page-opener-figure hero-figure"
          />

          <div className="page-opener-support">
            <p className="hero-lead">{copy.hero.lead}</p>

            <blockquote className="orientation-promise">
              <span>{copy.hero.promiseLabel}</span>
              {copy.hero.promise}
            </blockquote>

            <div className="hero-actions">
              <Link
                href="/#reader-routes"
                className="primary-action"
                onClick={() => trackAtlasEvent("reader_route_index_open", { source: "home_hero" })}
              >
                {copy.hero.start} <ArrowIcon />
              </Link>
              <Link href="/method" className="text-action">{copy.hero.method}</Link>
            </div>
          </div>
        </section>

        <section id="reader-routes" className="reader-routes wide-block" aria-labelledby="reader-routes-title">
          <header className="section-heading reader-routes-heading">
            <div>
              <p className="section-kicker">{copy.readerRoutes.kicker}</p>
              <h2 id="reader-routes-title">{copy.readerRoutes.title}</h2>
            </div>
            <p>{copy.readerRoutes.intro}</p>
          </header>

          <ReaderContinuityPanel />

          <div className="reader-route-grid">
            {readerRouteSpecs.map((route, routeIndex) => {
              const routeCopy = copy.readerRoutes.choices[route.id];
              const materials = route.materials.flatMap((material) => {
                const resolved = resolveReaderMaterial(material, chapterMap, essayMap);
                return resolved ? [resolved] : [];
              });
              const exercise = resolveReaderCrystal(route.exercise, chapterMap);
              if (!exercise || materials.length !== route.materials.length) return null;
              return (
                <article
                  id={`reader-route-${route.id}`}
                  className="reader-route-card"
                  aria-labelledby={`reader-route-${route.id}-title`}
                  key={route.id}
                >
                  <header className="reader-route-card-heading">
                    <span>{String(routeIndex + 1).padStart(2, "0")}</span>
                    <h3 id={`reader-route-${route.id}-title`}>{routeCopy.title}</h3>
                    <p>{routeCopy.lead}</p>
                    {routeCopy.caution && <p className="reader-route-caution">{routeCopy.caution}</p>}
                  </header>

                  <ol className="reader-route-materials">
                    {materials.map((material, materialIndex) => (
                      <li key={`${material.kind}-${material.href}`}>
                        <span>{copy.readerRoutes.step} {materialIndex + 1}</span>
                        <Link
                          href={material.href}
                          onClick={() => {
                            recordReaderStep(route.id, materialIndex + 1, material.reference);
                            trackAtlasEventOnce("reader_route_start", route.id, {
                              route_id: route.id,
                              first_step_index: materialIndex + 1,
                              metrics_version: 1,
                            });
                            trackAtlasEvent("reader_route_material_open", {
                              route_id: route.id,
                              material_kind: material.kind,
                              material_id: material.id,
                              step_index: materialIndex + 1,
                              metrics_version: 1,
                            });
                          }}
                        >
                          <small>
                            {material.kind === "essay"
                              ? `${copy.readerRoutes.essay} ${material.number} · ${material.readingTime}`
                              : `${copy.readerRoutes.chapter} ${material.number} · ${material.status}`}
                          </small>
                          <strong>{material.title}</strong>
                          <ArrowIcon />
                        </Link>
                      </li>
                    ))}
                  </ol>

                  <footer className="reader-route-practice">
                    <p className="section-kicker">{copy.readerRoutes.practice}</p>
                    <blockquote>{exercise.question}</blockquote>
                    <Link
                      href={exercise.href}
                      onClick={() => {
                        recordReaderStep(route.id, 5, route.exercise);
                        trackAtlasEvent("reader_route_practice_open", {
                          route_id: route.id,
                          material_kind: "crystal",
                          material_id: exercise.id,
                          metrics_version: 1,
                        });
                      }}
                    >
                      {copy.readerRoutes.context} <ArrowIcon />
                    </Link>
                  </footer>
                </article>
              );
            })}
          </div>
        </section>

        <section id="essays" className="home-essays wide-block" aria-labelledby="home-essay-journey-title">
          <div className="home-essay-journey">
            <header className="home-essay-journey-header">
              <div>
                <p className="section-kicker">{copy.essaysHeading.kicker}</p>
                <h2 id="home-essay-journey-title">{copy.essaysHeading.title}</h2>
              </div>
              <p>{copy.essaysHeading.intro}</p>
            </header>

            <p className="home-essay-route-summary">{copy.essaysHeading.route}</p>

            <div className="essay-index-list home-essay-index">
              {essays.map((essay, index) => {
                const step = copy.essaysHeading.steps[index];
                if (!step) return null;
                const audio = getEssayAudio(essay.slug);
                return (
                  <article className={essay.image ? "is-illustrated" : undefined} key={essay.slug}>
                    <span className="essay-index-number">{essay.number}</span>
                    <div className="essay-index-entry">
                      {essay.image && (
                        <span className="essay-index-thumb" aria-hidden="true">
                          <img
                            src={essay.image.src}
                            alt=""
                            width={essay.image.width}
                            height={essay.image.height}
                            loading="lazy"
                            decoding="async"
                          />
                        </span>
                      )}
                      <div className="essay-index-copy">
                        <p className="section-kicker">{step.stage} · {essay.kicker} · {essay.readingTime}</p>
                        {audio && <span className="essay-index-audio-badge"><HeadphonesIcon />{audio.copy[language].label}</span>}
                        <h3>{essay.title}</h3>
                        <p className="essay-index-stage">{step.text}</p>
                        <p>{essay.lead}</p>
                        <Link href={`/essay/${essay.slug}`}>{copy.essaysHeading.read} <ArrowIcon /></Link>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>

            <footer className="home-essay-journey-footer">
              <div className="hero-actions home-essay-actions">
                {essays[0] && <Link href={`/essay/${essays[0].slug}`} className="primary-action">{copy.essaysHeading.begin} <ArrowIcon /></Link>}
              </div>
            </footer>
          </div>
        </section>

        <section className="orientation reading-block">
          <p className="section-kicker">{copy.orientation.kicker}</p>
          <h2>{copy.orientation.title}</h2>
          <p className="orientation-lead">{copy.orientation.lead}</p>

          <div className="orientation-loop" aria-label={copy.orientation.loopAria}>
            <span>{copy.orientation.loop[0]}</span><i>→</i>
            <span>{copy.orientation.loop[1]}</span><i>→</i>
            <span>{copy.orientation.loop[2]}</span><i>→</i>
            <span>{copy.orientation.loop[3]}</span><i>→</i>
            <span>{copy.orientation.loop[4]}</span><i>→</i>
            <span>{copy.orientation.loop[5]}</span>
          </div>

          <div className="orientation-copy">
            {copy.orientation.paragraphs.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
          </div>

          <blockquote className="orientation-axiom">{copy.orientation.axiom}</blockquote>
        </section>

        <section className="manifesto reading-block">
          <p className="section-kicker">{copy.manifesto.kicker}</p>
          <h2>{copy.manifesto.title}</h2>
          <div className="manifesto-grid">
            <p className="manifesto-large">{copy.manifesto.large}</p>
            <div>{copy.manifesto.paragraphs.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}</div>
          </div>
        </section>

        <section className="distinction-section wide-block">
          <div className="section-heading">
            <div><p className="section-kicker">{copy.distinctionsHeading.kicker}</p><h2>{copy.distinctionsHeading.title}</h2></div>
            <p>{copy.distinctionsHeading.intro}</p>
          </div>
          <div className="distinction-atlas">
            {copy.distinctions.map(([left, right, text], index) => (
              <article key={left}>
                <span>{String(index + 1).padStart(2, "0")}</span>
                <h3>{left} <i>/</i> {right}</h3>
                <p>{text}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="parts-section wide-block">
          <div className="section-heading">
            <div><p className="section-kicker">{copy.partsHeading.kicker}</p><h2>{copy.partsHeading.title}</h2></div>
            <p>{copy.partsHeading.intro}</p>
          </div>
          <div className="part-cards">
            {parts.map((part) => {
              const partChapters = chapters.filter((chapter) => chapter.part === part.id);
              return (
                <article className="part-card" key={part.id}>
                  <div className="part-card-number">{part.number}</div>
                  <div className="part-card-main">
                    <h3><Link href={`/part/${part.id}`}>{part.title}</Link></h3>
                    <p>{part.description}</p>
                    <blockquote>{part.question}</blockquote>
                    <ol>{partChapters.map((chapter) => <li key={chapter.slug}><Link href={`/chapter/${chapter.slug}`}><span>{chapter.number}</span>{chapter.navTitle}</Link></li>)}</ol>
                  </div>
                </article>
              );
            })}
          </div>
        </section>

        <section className="tensions-section wide-block">
          <div className="section-heading">
            <div><p className="section-kicker">{copy.tensionsHeading.kicker}</p><h2>{copy.tensionsHeading.title}</h2></div>
            <p>{copy.tensionsHeading.intro}</p>
          </div>
          <div className="tension-grid">
            {copy.tensions.map((item) => <article key={item.pair}><span>{item.pair}</span><h3>{item.title}</h3><p>{item.text}</p></article>)}
          </div>
        </section>

        <section className="start-section reading-block">
          <p className="section-kicker">{copy.start.kicker}</p>
          <h2>{copy.start.title}</h2>
          <p>{copy.start.text}</p>
        </section>
      </article>
      <PageBeacon anchorRef={beaconAnchorRef} image={illustration} label={copy.hero.beaconLabel} title={copy.hero.title} />
      <PageFooter />
      <Lightbox image={image} onClose={() => setImage(null)} />
    </>
  );
}
